export interface Subject {
  name: string;
  icon: string; // lucide-react icon name or custom mapping
}

export interface ExamBoard {
  board: string;
  subjects: Subject[];
}

export interface ExamCategory {
  name: string;
  country?: string;
  boards: ExamBoard[];
}

export const examData: ExamCategory[] = [
  {
    name: 'Year R (Reception)', // Age 4–5
    country: 'UK', // Tagging existing as UK
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'Literacy', icon: 'Book' },
          { name: 'Numeracy', icon: 'Calculator' },
          { name: 'Art & Design', icon: 'Paintbrush' },
        ],
      },
    ],
  },
  // ... (Existing UK exams would be here, but I'm just appending to the end for the new ones, 
  // so I will rely on the "append" strategy for the tool or replacement chunks carefully.)
  // Actually, replace_file_content replaces a block. I should probably use multi_replace.
  // But since I'm touching the interface at the top AND the end of the array, I can do it in two chunks.

  {
    name: 'Year 1', // Age 5–6
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'English', icon: 'Book' },
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
        ],
      },
    ],
  },
  {
    name: 'Year 2 (KS1 SATs)', // Age 6–7
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'English', icon: 'Book' },
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
        ],
      },
    ],
  },
  {
    name: 'Year 3', // Age 7–8
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'English', icon: 'Book' },
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
          { name: 'History', icon: 'Landmark' },
          { name: 'Geography', icon: 'Globe' },
        ],
      },
    ],
  },
  {
    name: 'Year 4', // Age 8–9
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'English', icon: 'Book' },
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
          { name: 'History', icon: 'Landmark' },
          { name: 'Geography', icon: 'Globe' },
        ],
      },
    ],
  },
  {
    name: 'Year 5', // Age 9–10
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'English', icon: 'Book' },
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
          { name: 'History', icon: 'Landmark' },
          { name: 'Geography', icon: 'Globe' },
        ],
      },
    ],
  },
  {
    name: 'Year 6 (KS2 SATs)', // Age 10–11
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'English', icon: 'Book' },
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
        ],
      },
    ],
  },
  {
    name: 'Year 6 (11+ Exam)',
    boards: [
      {
        board: 'Independent Schools',
        subjects: [
          { name: 'Maths', icon: 'Calculator' },
          { name: 'English', icon: 'Book' },
          { name: 'Verbal Reasoning', icon: 'Type' },
          { name: 'Non-Verbal Reasoning', icon: 'Shapes' },
        ],
      },
    ],
  },
  {
    name: 'Year 7', // Age 11–12
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'English', icon: 'Book' },
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
          { name: 'History', icon: 'Landmark' },
          { name: 'Geography', icon: 'Globe' },
          { name: 'Design & Technology', icon: 'Cog' },
          { name: 'Computing', icon: 'Laptop' },
        ],
      },
    ],
  },
  {
    name: 'Year 8', // Age 12–13
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'English', icon: 'Book' },
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
          { name: 'History', icon: 'Landmark' },
          { name: 'Geography', icon: 'Globe' },
          { name: 'Design & Technology', icon: 'Cog' },
          { name: 'Computing', icon: 'Laptop' },
        ],
      },
    ],
  },
  {
    name: 'Year 9', // Age 13–14
    boards: [
      {
        board: 'National Curriculum',
        subjects: [
          { name: 'English', icon: 'Book' },
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
          { name: 'History', icon: 'Landmark' },
          { name: 'Geography', icon: 'Globe' },
          { name: 'Design & Technology', icon: 'Cog' },
          { name: 'Computing', icon: 'Laptop' },
        ],
      },
    ],
  },
  {
    name: 'GCSE',
    boards: [
      {
        board: 'AQA',
        subjects: [
          { name: 'Biology', icon: 'Leaf' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Physics', icon: 'Zap' },
          { name: 'Mathematics', icon: 'Calculator' },
          { name: 'English Language', icon: 'Type' },
          { name: 'English Literature', icon: 'Book' },
          { name: 'Geography', icon: 'Globe' },
          { name: 'Economics', icon: 'BarChart3' },
          { name: 'Business Studies', icon: 'Building2' },
          { name: 'Psychology', icon: 'Brain' },
          { name: 'Computer Science', icon: 'Laptop' },
          { name: 'Religious Studies', icon: 'Cross' },
          { name: 'Sociology', icon: 'Users' },
        ],
      },
      {
        board: 'Edexcel',
        subjects: [
          { name: 'Biology', icon: 'Leaf' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Physics', icon: 'Zap' },
          { name: 'Mathematics', icon: 'Calculator' },
          { name: 'Geography', icon: 'Globe' },
        ],
      },
      {
        board: 'OCR',
        subjects: [
          { name: 'Biology', icon: 'Leaf' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Physics', icon: 'Zap' },
          { name: 'Computer Science', icon: 'Laptop' },
        ],
      },
    ],
  },
  {
    name: 'AS Level',
    boards: [
      {
        board: 'AQA',
        subjects: [
          { name: 'Biology', icon: 'Leaf' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Physics', icon: 'Zap' },
          { name: 'Mathematics', icon: 'Calculator' },
          { name: 'Psychology', icon: 'Brain' },
          { name: 'Economics', icon: 'BarChart3' },
        ],
      },
    ],
  },
  {
    name: 'A Level',
    boards: [
      {
        board: 'AQA',
        subjects: [
          { name: 'Biology', icon: 'Leaf' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Physics', icon: 'Zap' },
          { name: 'Mathematics', icon: 'Calculator' },
          { name: 'Economics', icon: 'BarChart3' },
          { name: 'Psychology', icon: 'Brain' },
        ],
      },
      {
        board: 'OCR',
        subjects: [
          { name: 'Biology', icon: 'Leaf' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Physics', icon: 'Zap' },
        ],
      },
    ],
  },
  {
    name: 'International Baccalaureate (IB)',
    boards: [
      {
        board: 'IBO',
        subjects: [
          { name: 'Biology', icon: 'Leaf' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Physics', icon: 'Zap' },
          { name: 'Mathematics AA', icon: 'Calculator' },
          { name: 'Mathematics AI', icon: 'Calculator' },
          { name: 'Economics', icon: 'BarChart3' },
          { name: 'Psychology', icon: 'Brain' },
          { name: 'Business & Management', icon: 'Building2' },
          { name: 'Geography', icon: 'Globe' },
          { name: 'ESS', icon: 'TreeDeciduous' },
        ],
      },
    ],
  },
  {
    name: 'T Levels',
    boards: [
      {
        board: 'Pearson',
        subjects: [
          { name: 'Digital Production', icon: 'Laptop' },
          { name: 'Health', icon: 'HeartPulse' },
          { name: 'Education & Childcare', icon: 'School' },
        ],
      },
    ],
  },
  {
    name: 'BTEC',
    boards: [
      {
        board: 'Pearson',
        subjects: [
          { name: 'Business', icon: 'Building2' },
          { name: 'IT', icon: 'Laptop' },
          { name: 'Engineering', icon: 'Cog' },
          { name: 'Health & Social Care', icon: 'HeartPulse' },
        ],
      },
    ],
  },
  {
    name: 'NVQ',
    boards: [
      {
        board: 'City & Guilds',
        subjects: [
          { name: 'Construction', icon: 'Hammer' },
          { name: 'Hospitality', icon: 'Utensils' },
          { name: 'Health & Social Care', icon: 'HeartPulse' },
        ],
      },
    ],
  },
  {
    name: 'Scottish National 5',
    boards: [
      {
        board: 'SQA',
        subjects: [
          { name: 'Mathematics', icon: 'Calculator' },
          { name: 'English', icon: 'Book' },
        ],
      },
    ],
  },
  {
    name: 'Scottish Higher',
    boards: [
      {
        board: 'SQA',
        subjects: [
          { name: 'Biology', icon: 'Leaf' },
          { name: 'Chemistry', icon: 'FlaskConical' },
        ],
      },
    ],
  },
  {
    name: 'Scottish Advanced Higher',
    boards: [
      {
        board: 'SQA',
        subjects: [
          { name: 'Physics', icon: 'Zap' },
          { name: 'Mathematics', icon: 'Calculator' },
        ],
      },
    ],
  },
  {
    name: 'Cambridge Pre-U',
    boards: [
      {
        board: 'CIE',
        subjects: [{ name: 'Global Perspectives', icon: 'Globe' }],
      },
    ],
  },
  {
    name: 'Core Maths',
    boards: [
      {
        board: 'Various',
        subjects: [{ name: 'Mathematics', icon: 'Calculator' }],
      },
    ],
  },
  {
    name: 'Functional Skills',
    boards: [
      {
        board: 'Various',
        subjects: [
          { name: 'Maths', icon: 'Calculator' },
          { name: 'English', icon: 'Book' },
        ],
      },
    ],
  },
  {
    name: 'EPQ (Extended Project Qualification)',
    boards: [
      {
        board: 'AQA',
        subjects: [{ name: 'Project Work', icon: 'ClipboardList' }],
      },
    ],
  },
  {
    name: 'SAT (UK usage)',
    boards: [
      {
        board: 'College Board',
        subjects: [
          { name: 'Maths', icon: 'Calculator' },
          { name: 'English', icon: 'Book' },
        ],
      },
    ],
  },
  {
    name: 'ACT (UK usage)',
    boards: [
      {
        board: 'ACT Org',
        subjects: [
          { name: 'Maths', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
        ],
      },
    ],
  },
  {
    name: 'IELTS',
    boards: [
      {
        board: 'Cambridge/IDP',
        subjects: [
          { name: 'Listening', icon: 'Headphones' },
          { name: 'Reading', icon: 'Book' },
          { name: 'Writing', icon: 'Type' },
          { name: 'Speaking', icon: 'Mic' },
        ],
      },
    ],
  },
  {
    name: 'TOEFL',
    boards: [
      {
        board: 'ETS',
        subjects: [
          { name: 'Reading', icon: 'Book' },
          { name: 'Listening', icon: 'Headphones' },
          { name: 'Speaking', icon: 'Mic' },
          { name: 'Writing', icon: 'Type' },
        ],
      },
    ],
  },
  {
    name: 'Cambridge English Exams',
    boards: [
      {
        board: 'Cambridge',
        subjects: [
          { name: 'FCE', icon: 'Book' },
          { name: 'CAE', icon: 'BookOpen' },
          { name: 'CPE', icon: 'GraduationCap' },
        ],
      },
    ],
  },
  {
    name: '11+',
    boards: [
      {
        board: 'Independent Schools',
        subjects: [
          { name: 'Maths', icon: 'Calculator' },
          { name: 'English', icon: 'Book' },
        ],
      },
    ],
  },
  {
    name: '13+ Common Entrance Exam',
    boards: [
      {
        board: 'ISEB',
        subjects: [
          { name: 'Maths', icon: 'Calculator' },
          { name: 'English', icon: 'Book' },
          { name: 'Science', icon: 'FlaskConical' },
        ],
      },
    ],
  },
  {
    name: 'UCAT',
    boards: [
      {
        board: 'UKCAT Consortium',
        subjects: [
          { name: 'Verbal Reasoning', icon: 'Type' },
          { name: 'Quantitative Reasoning', icon: 'Calculator' },
          { name: 'Abstract Reasoning', icon: 'Shapes' },
        ],
      },
    ],
  },
  {
    name: 'BMAT',
    boards: [
      {
        board: 'Cambridge Assessment',
        subjects: [
          { name: 'Aptitude', icon: 'Activity' },
          { name: 'Scientific Knowledge', icon: 'FlaskConical' },
        ],
      },
    ],
  },
  {
    name: 'LNAT',
    boards: [
      {
        board: 'LNAT Consortium',
        subjects: [
          { name: 'Comprehension', icon: 'BookOpen' },
          { name: 'Essay Writing', icon: 'Type' },
        ],
      },
    ],
  },
  {
    name: 'STEP',
    boards: [
      {
        board: 'Cambridge',
        subjects: [{ name: 'Mathematics', icon: 'Calculator' }],
      },
    ],
  },
  {
    name: 'MAT',
    boards: [
      {
        board: 'Oxford',
        subjects: [{ name: 'Mathematics', icon: 'Calculator' }],
      },
    ],
  },
  {
    name: 'HAT',
    boards: [
      {
        board: 'Oxford',
        subjects: [{ name: 'History', icon: 'Landmark' }],
      },
    ],
  },
  {
    name: 'PAT',
    boards: [
      {
        board: 'Oxford',
        subjects: [
          { name: 'Physics', icon: 'Zap' },
          { name: 'Maths', icon: 'Calculator' },
        ],
      },
    ],
  },
  // --- INDIA EXAMS ---
  {
    name: 'Class 10',
    country: 'India',
    boards: [
      {
        board: 'CBSE',
        subjects: [
          { name: 'Mathematics', icon: 'Calculator' },
          { name: 'Science', icon: 'FlaskConical' },
          { name: 'Social Science', icon: 'Globe' },
          { name: 'English', icon: 'Book' },
          { name: 'Hindi', icon: 'Type' },
        ],
      },
      {
        board: 'ICSE',
        subjects: [
          { name: 'Mathematics', icon: 'Calculator' },
          { name: 'Physics', icon: 'Zap' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Biology', icon: 'Leaf' },
          { name: 'History & Civics', icon: 'Landmark' },
          { name: 'Geography', icon: 'Globe' },
          { name: 'English', icon: 'Book' },
        ],
      },
    ],
  },
  {
    name: 'Class 12',
    country: 'India',
    boards: [
      {
        board: 'CBSE (Science)',
        subjects: [
          { name: 'Physics', icon: 'Zap' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Mathematics', icon: 'Calculator' },
          { name: 'Biology', icon: 'Leaf' },
          { name: 'Computer Science', icon: 'Laptop' },
          { name: 'English', icon: 'Book' },
        ],
      },
      {
        board: 'CBSE (Commerce)',
        subjects: [
          { name: 'Accountancy', icon: 'BarChart3' },
          { name: 'Business Studies', icon: 'Building2' },
          { name: 'Economics', icon: 'BarChart3' },
          { name: 'Mathematics', icon: 'Calculator' },
          { name: 'English', icon: 'Book' },
        ],
      },
    ],
  },
  {
    name: 'Engineering Entrance',
    country: 'India',
    boards: [
      {
        board: 'NTA (JEE)',
        subjects: [
          { name: 'JEE Main - Physics', icon: 'Zap' },
          { name: 'JEE Main - Chemistry', icon: 'FlaskConical' },
          { name: 'JEE Main - Mathematics', icon: 'Calculator' },
          { name: 'JEE Advanced - Physics', icon: 'Zap' },
          { name: 'JEE Advanced - Chemistry', icon: 'FlaskConical' },
          { name: 'JEE Advanced - Mathematics', icon: 'Calculator' },
        ],
      },
      {
        board: 'BITS',
        subjects: [
          { name: 'BITSAT', icon: 'Laptop' },
        ],
      },
    ],
  },
  {
    name: 'Medical Entrance',
    country: 'India',
    boards: [
      {
        board: 'NTA (NEET)',
        subjects: [
          { name: 'Physics', icon: 'Zap' },
          { name: 'Chemistry', icon: 'FlaskConical' },
          { name: 'Biology (Botany & Zoology)', icon: 'Leaf' },
        ],
      },
    ],
  },
  {
    name: 'Civil Services & Govt',
    country: 'India',
    boards: [
      {
        board: 'UPSC',
        subjects: [
          { name: 'General Studies', icon: 'Globe' },
          { name: 'CSAT', icon: 'Brain' },
          { name: 'History', icon: 'Landmark' },
          { name: 'Geography', icon: 'Globe' },
          { name: 'Polity', icon: 'Landmark' },
          { name: 'Economics', icon: 'BarChart3' },
        ],
      },
    ],
  },
  {
    name: 'Common Entrance',
    country: 'India',
    boards: [
      {
        board: 'CUET',
        subjects: [
          { name: 'General Test', icon: 'Brain' },
          { name: 'Domain Subjects', icon: 'Book' },
        ],
      },
      {
        board: 'CLAT',
        subjects: [
          { name: 'Legal Reasoning', icon: 'Scale' }, // Assuming Scale exists or mapped, else default
          { name: 'Logical Reasoning', icon: 'Brain' },
        ],
      },
    ],
  },
];
